import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import type { ModifyMapSetRequest } from '../../types/case-container'
import { addToMapSet, removeFromMapSet } from '../../services/map-set'
import { logger } from '../../utils/logger'

const DEBOUNCE_MS = 500

export interface PendingChanges {
  add: Set<string>
  remove: Set<string>
}

export interface MapSetSync {
  syncing: boolean
  pendingChanges: MutableRefObject<PendingChanges>
  debounceTimer: MutableRefObject<NodeJS.Timeout | null>
  scheduleSync: () => void
  setSyncing: (value: boolean) => void
  setError: (value: string | null) => void
}

interface UseMapSetSyncArgs {
  caseId: string | null
  setError: (value: string | null) => void
}

export function useMapSetSync({ caseId, setError }: UseMapSetSyncArgs): MapSetSync {
  const [syncing, setSyncing] = useState(false)
  const pendingChanges = useRef<PendingChanges>({ add: new Set(), remove: new Set() })
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const syncToBackend = useCallback(async () => {
    if (!caseId) return
    const changes = pendingChanges.current
    pendingChanges.current = { add: new Set(), remove: new Set() }
    if (changes.add.size === 0 && changes.remove.size === 0) return
    setSyncing(true)
    try {
      if (changes.add.size > 0) {
        const request: ModifyMapSetRequest = { endpointIds: Array.from(changes.add) }
        await addToMapSet(caseId, request)
      }
      if (changes.remove.size > 0) {
        const request: ModifyMapSetRequest = { endpointIds: Array.from(changes.remove) }
        await removeFromMapSet(caseId, request)
      }
      setError(null)
    } catch (err) {
      logger.warn('useMapSet', 'Failed to sync map set to backend', err)
      setError(err instanceof Error ? err.message : 'Failed to sync map set')
    } finally {
      setSyncing(false)
    }
  }, [caseId, setError])

  const scheduleSync = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      void syncToBackend()
    }, DEBOUNCE_MS)
  }, [syncToBackend])

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  return { syncing, pendingChanges, debounceTimer, scheduleSync, setSyncing, setError }
}
